/**
 * usuariosApi — gestão de usuários e perfis (M1, RF-AUT-04). Rotas admin.
 *
 * CONTRATO CONGELADO (BINDING; envelope `{ data }`):
 *  - GET   /usuarios        → { data: Usuario[], nextCursor: string|null }
 *  - POST  /usuarios        → { data: UsuarioCriado }  (status: convidado)
 *  - PATCH /usuarios/{id}    → { data: UsuarioAtualizado }
 *  - DELETE /usuarios/{id}   → { data: { id, status: 'excluido' } }        (ADR-052)
 *  - POST  /usuarios/{id}/redefinir-mfa → { data: { id } }               (ADR-052)
 *
 * Nenhum dado pessoal além do e-mail (necessário à gestão) trafega; nada é
 * logado (RN-M11-001). O JWT admin é anexado pelo ApiClient.
 */

import { ApiClient } from './apiClient';

export type Perfil = 'consulta' | 'editor' | 'admin';
export type StatusUsuario = 'ativo' | 'desativado';

/** Item de GET /usuarios. */
export interface Usuario {
  readonly id: string;
  readonly email: string;
  readonly perfil: Perfil;
  readonly status: StatusUsuario;
}

/** Resposta do POST /usuarios (usuário recém-convidado). */
export interface UsuarioCriado {
  readonly id: string;
  readonly email: string;
  readonly perfil: string;
  readonly status: 'convidado';
}

/** Resposta do PATCH /usuarios/{id}. */
export interface UsuarioAtualizado {
  readonly id: string;
  readonly perfil: string;
  readonly status: string;
}

/** Body do POST /usuarios. */
export interface CriarUsuarioInput {
  readonly email: string;
  readonly perfil: Perfil;
}

/** Body do PATCH /usuarios/{id} (campos opcionais). */
export interface AtualizarUsuarioInput {
  readonly perfil?: Perfil;
  readonly status?: StatusUsuario;
}

/** Página de listagem (cursor-based). */
export interface ListaUsuarios {
  readonly data: readonly Usuario[];
  readonly nextCursor: string | null;
}

interface Envelope<T> {
  readonly data: T;
}
interface ListaEnvelope<T> {
  readonly data: readonly T[];
  readonly nextCursor: string | null;
}

export class UsuariosApi {
  constructor(private readonly client: ApiClient) {}

  /** GET /usuarios — lista usuários (admin). */
  async listar(cursor?: string): Promise<ListaUsuarios> {
    const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
    const resp = await this.client.request<ListaEnvelope<Usuario>>(`/usuarios${qs}`);
    return { data: resp.data, nextCursor: resp.nextCursor };
  }

  /** POST /usuarios — convida um novo usuário (admin). */
  async criar(input: CriarUsuarioInput): Promise<UsuarioCriado> {
    const resp = await this.client.request<Envelope<UsuarioCriado>>('/usuarios', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return resp.data;
  }

  /** DELETE /usuarios/{id} — exclui o usuário (admin; nunca o próprio nem o último admin — 409). */
  async excluir(id: string): Promise<{ id: string; status: 'excluido' }> {
    const resp = await this.client.request<Envelope<{ id: string; status: 'excluido' }>>(
      `/usuarios/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    );
    return resp.data;
  }

  /** POST /usuarios/{id}/redefinir-mfa — o próximo login cadastra um autenticador novo (admin). */
  async redefinirMfa(id: string): Promise<{ id: string }> {
    const resp = await this.client.request<Envelope<{ id: string }>>(
      `/usuarios/${encodeURIComponent(id)}/redefinir-mfa`,
      { method: 'POST' },
    );
    return resp.data;
  }

  /** PATCH /usuarios/{id} — altera perfil ou status (admin). */
  async atualizar(id: string, input: AtualizarUsuarioInput): Promise<UsuarioAtualizado> {
    const resp = await this.client.request<Envelope<UsuarioAtualizado>>(
      `/usuarios/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: JSON.stringify(input) },
    );
    return resp.data;
  }
}
